import { useEffect, useState } from 'react';
import { useUpdateCastRelationship } from '@pocapan/shared/domains/scene/ui/hooks/useUpdateCastRelationship';
import type { components } from '@pocapan/shared/types/schema';
import type { Gender } from '../types';

type ApiV1CastRelationship = components['schemas']['ApiV1CastRelationship'];

interface ThirdPersonaForm {
  name: string;
  gender: Gender | undefined;
}

interface UseUpdateThirdPersonaParams {
  currentCastRelationship: ApiV1CastRelationship | null | undefined;
}

export function useUpdateThirdPersona({ currentCastRelationship }: UseUpdateThirdPersonaParams) {
  const [isOpen, setIsOpen] = useState(false);
  const [thirdPersona, setThirdPersona] = useState<ThirdPersonaForm>({
    name: '',
    gender: undefined
  });

  const { mutateAsync: updateCastRelationship, isLoading: isUpdating } =
    useUpdateCastRelationship();

  useEffect(() => {
    if (isOpen) {
      setThirdPersona({
        name: currentCastRelationship?.personC?.name ?? '',
        gender: currentCastRelationship?.personC?.gender as Gender | undefined
      });
    }
  }, [isOpen, currentCastRelationship?.personC?.name, currentCastRelationship?.personC?.gender]);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const setThirdPersonaName = (name: string) =>
    setThirdPersona((prev) => ({ ...prev, name }));
  const setThirdPersonaGender = (gender: Gender) =>
    setThirdPersona((prev) => ({ ...prev, gender }));

  const handleUpdate = async () => {
    const castRelationshipId = currentCastRelationship?.castRelationshipId ?? '';
    const existingName = currentCastRelationship?.personC?.name ?? '';
    if (!thirdPersona.name.trim() || !castRelationshipId) return;
    if (
      thirdPersona.name.trim() === existingName &&
      thirdPersona.gender === currentCastRelationship?.personC?.gender
    ) {
      close();
      return;
    }
    try {
      const response = await updateCastRelationship({
        castRelationshipId,
        body: {
          cast_relationship: {
            person_c: {
              name: thirdPersona.name.trim(),
              gender: thirdPersona.gender
            }
          }
        }
      });
      if (response?.castRelationshipId) {
        close();
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: mutation failure should be visible during QA/debug
      console.error(error);
    }
  };

  return {
    isOpen,
    open,
    close,
    thirdPersona,
    setThirdPersonaName,
    setThirdPersonaGender,
    handleUpdate,
    isUpdating
  };
}
